import { Fragment, h, render } from "preact";
import { useEffect, useState, useRef } from "preact/hooks";
import { Thumbs, EffectFade } from "swiper/modules";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { CloseIcon, FaqIcon, WarrantyIcon } from "../helpers/icons.jsx";

import storefront from "../helpers/storefront";
import * as queries from "../helpers/storefront/queries";

const MediaContent = ({ data }) => {
  const [swiper, setSwiper] = useState(null);
  const [modalSwiper, setModalSwiper] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const swiperThumbsRef = useRef(null);
  const [product, setProduct] = useState(data.product);
  const [currentVariant, setCurrentVariant] = useState(product.variants[0]);
  const [productHandle, setProductHandle] = useState(null);
  const [media, setMedia] = useState(product.media);
  const [mediaFade, setMediaFade] = useState(false);
  const [galleryFade, setGalleryFade] = useState(false);
  const badge = product.tags?.find((t) => t.includes("badge:"))?.split(":");
  let screenMobile = window.matchMedia("(max-width: 992px)");

  const variantImages = product.variants.map(
    (edge) => edge.featured_image?.src
  );
  const [variantImage, setVariantImage] = useState(
    product?.variants[0]?.featured_image?.src
  );

  const images = media?.filter((i) => {
    return !variantImages.find((v) => {
      return v === i.src;
    });
  });

  const [filteredMedia, setFilteredMedia] = useState(images);

  const swiperModalParams = {
    slidesPerView: "auto",
    spaceBetween: 0,
    direction: "horizontal",
    effect: "fade",
    thumbs: {
      swiper: swiperThumbsRef.current,
    },
  };
  const swiperModalThumbParams = {
    direction: "horizontal",
    spaceBetween: 20,
    slidesPerView: "auto",
    watchSlidesProgress: true,
    centerInsufficientSlides: true,
    slidesPerGroup: 1,
    breakpoints: {
      768: {
        direction: "vertical",
      },
    },
    grid: {
      rows: 1,
    },
  };
  const swiperParams = {
    slidesPerView: 1,
    effect: "fade",
    fadeEffect: { crossFade: true },
    loop: true,
    rebuildOnUpdate: true,
  };

  document.addEventListener(
    "productUpdate",
    (e) => {
      setMediaFade(true);
      setTimeout(() => {
        let v = e?.detail?.variant,
          p = e.detail.product;
        setCurrentVariant(v);
        v?.featured_image?.src && setVariantImage(v.featured_image.src);
        setProductHandle(p);
        setMediaFade(false);
      }, 500);
    },
    false
  );

  useEffect(async () => {
    if (!productHandle) return;
    setGalleryFade(true);
    const response = await storefront.request({
      query: queries.product,
      variables: {
        handle: productHandle,
      },
    });

    if (!response?.data?.productByHandle) return;
    let sp = response.data.productByHandle,
      si = sp?.images.edges,
      v = sp?.variants.edges,
      vi = v && v[0]?.image?.src;
    let updatedImages = si?.filter((i) => {
      return !v.find((vi) => {
        return vi.node.src === i.node.src;
      });
    });
    updatedImages = updatedImages.map((i) => i.node);
    setFilteredMedia(updatedImages);
    setTimeout(() => {
      setGalleryFade(false);
    }, 200);
  }, [productHandle]);

  useEffect(() => {
    checkSwiper();
  }, [swiper]);

  useEffect(() => {
    checkModalSwiper();
  }, [modalSwiper]);

  const checkModalSwiper = () => {
    document
      .querySelectorAll('[data-modal-trigger="product-gallery-zoom-modal"]')
      .forEach((item) => {
        item.addEventListener("click", () => {
          modalSwiper && modalSwiper.slideTo(item.dataset.slide);
        });
      });
  };

  const checkSwiper = () => {
    if (!screenMobile.matches) {
      if (swiper) {
        swiper.destroy(true, true);
      }
      return;
    }
  };

  window.addEventListener("resize", () => {
    checkSwiper();
  });

  return (
    <Fragment>
      {!currentVariant.available ? (
        <p className="product-main__badge oos">{data.message}</p>
      ) : (
        badge && (
          <div className={`product-main__badge ${badge[1]}`} data-product-badge>
            {badge[2]}
          </div>
        )
      )}
      {data.faqs && (
        <a className="product-main__faqs-badge caption" href="#productFaqs">
          <FaqIcon />
          FAQ
        </a>
      )}
      <Swiper
        {...swiperParams}
        modules={[EffectFade]}
        onSwiper={setSwiper}
        className="product-media__container"
        data-product-media-gallery
      >
        {variantImage && (
          <SwiperSlide
            data-product-image
            data-slide={0}
            data-modal-trigger="product-gallery-zoom-modal"
          >
            <img
              src={getSizedImageUrl(variantImage, "1200x")}
              alt={"Alt Text"}
              className={mediaFade ? "fade" : ""}
            />
            {data.warranty && (
              <div className="product-main__warranty copy-text">
                <WarrantyIcon />
                {data.warranty}
              </div>
            )}
          </SwiperSlide>
        )}
        {filteredMedia.map((item, index) => (
          <SwiperSlide
            data-product-image
            data-slide={variantImage ? index + 1 : index}
            data-modal-trigger="product-gallery-zoom-modal"
          >
            {index === 0 && !variantImage && data.warranty && (
              <div className="product-main__warranty copy-text">
                <WarrantyIcon />
                {data.warranty}
              </div>
            )}
            <img
              src={
                index === 0 && !variantImage
                  ? getSizedImageUrl(item?.src, "1200x")
                  : getSizedImageUrl(item?.src, "600x")
              }
              height={600 * (item ? item?.aspect_ratio : 1)}
              width={600}
              alt={`${item?.alt}`}
              loading={index === 0 && !variantImage ? false : "false"}
              className={galleryFade ? "fade" : ""}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <modal-component
        className="modal overlay"
        data-id="product-gallery-zoom-modal"
        data-modal
      >
        <div className="modal__overlay" data-modal-close></div>
        <div className="modal__content">
          <button
            className="modal__btn-close"
            aria-label="Close"
            data-modal-close
          >
            <CloseIcon />
          </button>
          <div className="modal__content__inner" data-modal-content>
            <Swiper
              {...swiperModalParams}
              onSwiper={setModalSwiper}
              modules={[EffectFade, Thumbs]}
              thumbs={{ swiper: thumbsSwiper }}
              className="product-media__modal"
              data-product-modal-slider
            >
              {variantImage && (
                <SwiperSlide
                  data-product-image
                  data-slide={0}
                  data-modal-trigger="product-gallery-zoom-modal"
                >
                  <img
                    src={getSizedImageUrl(variantImage, "1200x")}
                    height={1200}
                    width={1200}
                    loading="lazy"
                  />
                </SwiperSlide>
              )}
              {filteredMedia.map((item, index) => (
                <SwiperSlide
                  data-product-image
                  data-slide={index}
                  data-modal-trigger="product-gallery-zoom-modal"
                  onSwiper={setModalSwiper}
                >
                  <img
                    src={getSizedImageUrl(item?.src, "1200x")}
                    height={1200 * (item ? item?.aspect_ratio : 1)}
                    width={1200}
                    alt={`${item?.alt}`}
                    loading="lazy"
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <Swiper
              {...swiperModalThumbParams}
              modules={[EffectFade]}
              className="product-media__thumbs"
              onSwiper={setThumbsSwiper}
              data-product-modal-slider
            >
              {variantImage && (
                <SwiperSlide>
                  <img
                    src={getSizedImageUrl(variantImage, "80x")}
                    alt={"Alt Text"}
                    height="80"
                    width="80"
                    loading="lazy"
                  />
                </SwiperSlide>
              )}
              {filteredMedia.map((item, index) => (
                <SwiperSlide>
                  <img
                    src={getSizedImageUrl(item?.src, "1200x")}
                    alt={`${item?.alt}`}
                    height="80"
                    width="80"
                    loading="lazy"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </modal-component>
    </Fragment>
  );
};

export default MediaContent;
